import React, { useEffect, useState } from "react";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Loader2,
    RefreshCw,
    Sparkles,
    Wrench,
    ArrowRight,
    ShieldCheck,
    Info,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUS_STYLES = {
    Good: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
    "Needs Improvement":
        "text-amber-300 border-amber-500/30 bg-amber-500/10",
    Critical: "text-rose-300 border-rose-500/30 bg-rose-500/10",
};
const STATUS_ICON = {
    Good: CheckCircle2,
    "Needs Improvement": AlertTriangle,
    Critical: XCircle,
};

const PROV_STYLES = {
    "Publicly Discovered Data":
        "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
    "Live Connected Data":
        "text-cyan-300 border-cyan-500/30 bg-cyan-500/10",
    "AI Analysis": "text-indigo-300 border-indigo-500/30 bg-indigo-500/10",
    "AI Recommendation":
        "text-indigo-300 border-indigo-500/30 bg-indigo-500/10",
    "Awaiting Approval":
        "text-amber-300 border-amber-500/30 bg-amber-500/10",
    "Successfully Updated":
        "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
};

function scoreColor(s) {
    if (s >= 75) return "text-emerald-300";
    if (s >= 45) return "text-amber-300";
    return "text-rose-300";
}

export default function Audit() {
    const { currentBusiness } = useApp();
    const [params] = useSearchParams();
    const justOnboarded = params.get("just_onboarded") === "1";
    const [audit, setAudit] = useState(null);
    const [labels, setLabels] = useState({});
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [busyIssue, setBusyIssue] = useState(null);
    const [busyAll, setBusyAll] = useState(false);
    const nav = useNavigate();

    const load = async () => {
        if (!currentBusiness) return;
        setLoading(true);
        try {
            const { audit: a, section_labels } = await api.auditLatest(
                currentBusiness.id,
            );
            setAudit(a);
            setLabels(section_labels || {});
            if (!a) await runNew(false);
        } catch (e) {
            toast.error("Couldn't load audit.");
        } finally {
            setLoading(false);
        }
    };

    const runNew = async (showToast = true) => {
        if (!currentBusiness) return;
        setRunning(true);
        try {
            const { audit: a } = await api.auditRun(currentBusiness.id);
            setAudit(a);
            if (showToast) toast.success("New audit generated.");
        } catch {
            toast.error("Audit failed.");
        } finally {
            setRunning(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBusiness?.id]);

    const fixIssue = async (issueId) => {
        setBusyIssue(issueId);
        try {
            await api.auditFix(audit.id, issueId);
            toast.success("Fix draft sent to Approval Inbox.");
        } catch (e) {
            toast.error("Fix failed.");
        } finally {
            setBusyIssue(null);
        }
    };

    const fixAll = async () => {
        setBusyAll(true);
        try {
            const { generated } = await api.auditFixAll(audit.id, true);
            toast.success(
                `${generated} fixes queued in Approval Inbox (safe fields only).`,
            );
        } catch {
            toast.error("Bulk fix failed.");
        } finally {
            setBusyAll(false);
        }
    };

    if (loading && !audit)
        return (
            <div className="text-center py-16 text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                <div className="mt-3 text-sm">
                    Running your initial AI audit…
                </div>
            </div>
        );

    if (!audit)
        return (
            <div className="text-center py-24">
                <Sparkles className="h-8 w-8 text-indigo-400 mx-auto" />
                <div className="mt-4 text-slate-200 font-semibold">
                    No audit yet.
                </div>
                <Button
                    onClick={() => runNew(true)}
                    disabled={running}
                    data-testid="audit-run-button"
                    className="mt-6 bg-indigo-500 hover:bg-indigo-400 text-white gap-2"
                >
                    {running ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Sparkles className="h-4 w-4" />
                    )}
                    Run AI audit
                </Button>
            </div>
        );

    const sections = audit.section_scores || {};
    const sectionKeys = Object.keys(sections);

    return (
        <div
            className="max-w-6xl mx-auto space-y-8 fade-in"
            data-testid="audit-page"
        >
            {justOnboarded && (
                <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-sm text-emerald-100 flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                        Company created and initial audit is ready. Every
                        finding is labelled with its data source — nothing has
                        been changed on any external platform yet.
                    </div>
                </div>
            )}

            {/* Overall header */}
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow text-indigo-300">
                        AI Business Audit · {currentBusiness?.name}
                    </div>
                    <h1 className="mt-2 text-3xl font-extrabold text-slate-50 tracking-tight">
                        Digital health snapshot
                    </h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        onClick={() => runNew(true)}
                        disabled={running}
                        data-testid="audit-regenerate-button"
                        className="border-white/10 bg-white/[0.03] hover:bg-white/[0.08] gap-1.5"
                    >
                        {running ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                        Re-run audit
                    </Button>
                    <Button
                        onClick={fixAll}
                        disabled={busyAll}
                        data-testid="audit-fix-all-button"
                        className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5"
                    >
                        {busyAll ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Wrench className="h-4 w-4" />
                        )}
                        Fix all safe issues
                    </Button>
                </div>
            </div>

            {/* Overall score + provenance summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div
                    className="surface rounded-2xl p-6 text-center"
                    data-testid="audit-overall-score"
                >
                    <div className="label-eyebrow text-indigo-300">
                        Overall digital health
                    </div>
                    <div
                        className={`mt-3 text-6xl font-black tracking-tight ${scoreColor(
                            audit.overall_score,
                        )}`}
                    >
                        {audit.overall_score}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        out of 100
                    </div>
                </div>
                <div className="md:col-span-2 surface rounded-2xl p-6">
                    <div className="label-eyebrow text-indigo-300 mb-3">
                        Provenance summary
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(audit.provenance_summary || {}).map(
                            ([k, v]) => (
                                <Badge
                                    key={k}
                                    className={`border ${
                                        PROV_STYLES[k] ||
                                        "border-white/10 text-slate-400"
                                    }`}
                                    data-testid={`audit-prov-summary-${k
                                        .replace(/\s+/g, "-")
                                        .toLowerCase()}`}
                                >
                                    <Info className="h-3 w-3 mr-1" />
                                    {v} × {k}
                                </Badge>
                            ),
                        )}
                    </div>
                    <div className="mt-4 text-xs text-slate-400 leading-relaxed">
                        Every finding below carries the label of the source
                        used to derive it. No external platform has been
                        changed — approved fixes update your AI memory or your
                        Reputation AI profile only, and produce copy-paste content
                        where a live connector is not yet available.
                    </div>
                </div>
            </div>

            {/* Section grid */}
            <div>
                <h2 className="text-lg font-bold text-slate-100 mb-3">
                    Section scores
                </h2>
                <div
                    className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
                    data-testid="audit-section-grid"
                >
                    {sectionKeys.map((k) => {
                        const sec = sections[k];
                        const StatusIcon = STATUS_ICON[sec.status] || Info;
                        return (
                            <div
                                key={k}
                                data-testid={`audit-section-${k}`}
                                className="surface rounded-xl p-4"
                            >
                                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
                                    {labels[k] || k}
                                </div>
                                <div
                                    className={`mt-2 text-3xl font-black tracking-tight ${scoreColor(
                                        sec.score,
                                    )}`}
                                >
                                    {sec.score}
                                </div>
                                <Badge
                                    variant="outline"
                                    className={`mt-2 gap-1 ${
                                        STATUS_STYLES[sec.status] || ""
                                    }`}
                                >
                                    <StatusIcon className="h-3 w-3" />{" "}
                                    {sec.status}
                                </Badge>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Action plan */}
            {audit.action_plan?.length > 0 && (
                <div>
                    <h2 className="text-lg font-bold text-slate-100 mb-3">
                        Prioritised action plan
                    </h2>
                    <div className="space-y-2" data-testid="audit-action-plan">
                        {audit.action_plan.map((a, i) => (
                            <div
                                key={i}
                                data-testid={`action-plan-item-${i}`}
                                className="surface rounded-xl p-4 flex items-center justify-between gap-3"
                            >
                                <div>
                                    <div className="flex items-center gap-2">
                                        <Badge
                                            className={
                                                a.impact === "High"
                                                    ? "bg-rose-500/15 border border-rose-500/30 text-rose-200"
                                                    : "bg-amber-500/15 border border-amber-500/30 text-amber-200"
                                            }
                                        >
                                            {a.impact} impact
                                        </Badge>
                                        <span className="text-sm font-semibold text-slate-100">
                                            {a.title}
                                        </span>
                                    </div>
                                    <div className="text-[11px] mono text-slate-500 mt-1 uppercase tracking-widest">
                                        {labels[a.section] || a.section}
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    data-testid={`action-plan-fix-${i}`}
                                    onClick={() => fixIssue(a.issue_id)}
                                    disabled={busyIssue === a.issue_id}
                                    className={
                                        a.action_kind === "fix_with_ai"
                                            ? "bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5"
                                            : "bg-white/[0.05] hover:bg-white/10 border border-white/10 text-slate-100 gap-1.5"
                                    }
                                >
                                    {busyIssue === a.issue_id ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <Wrench className="h-3.5 w-3.5" />
                                    )}
                                    {a.action_label}
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Issues by section */}
            <div className="space-y-6">
                <h2 className="text-lg font-bold text-slate-100">
                    Detailed findings
                </h2>
                {sectionKeys.map((sk) => {
                    const secIssues = (audit.issues || []).filter(
                        (i) => i.section === sk,
                    );
                    if (secIssues.length === 0) return null;
                    return (
                        <div key={sk} data-testid={`audit-section-issues-${sk}`}>
                            <div className="label-eyebrow text-slate-400 mb-2">
                                {labels[sk] || sk}
                            </div>
                            <div className="space-y-3">
                                {secIssues.map((iss) => (
                                    <div
                                        key={iss.id}
                                        data-testid={`audit-issue-${iss.id}`}
                                        className="surface rounded-2xl p-5 space-y-3"
                                    >
                                        <div className="flex items-start gap-3 flex-wrap">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-bold text-slate-100">
                                                        {iss.title}
                                                    </span>
                                                    <Badge
                                                        variant="outline"
                                                        className={`gap-1 ${
                                                            STATUS_STYLES[
                                                                iss.status
                                                            ] || ""
                                                        }`}
                                                    >
                                                        {iss.status}
                                                    </Badge>
                                                    <Badge
                                                        variant="outline"
                                                        className={`gap-1 ${
                                                            PROV_STYLES[
                                                                iss.provenance
                                                            ] || ""
                                                        }`}
                                                    >
                                                        <Info className="h-3 w-3" />
                                                        {iss.provenance}
                                                    </Badge>
                                                </div>
                                            </div>
                                            {iss.ai_fixable && (
                                                <Button
                                                    size="sm"
                                                    data-testid={`audit-fix-with-ai-${iss.id}`}
                                                    onClick={() =>
                                                        fixIssue(iss.id)
                                                    }
                                                    disabled={
                                                        busyIssue === iss.id
                                                    }
                                                    className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5"
                                                >
                                                    {busyIssue === iss.id ? (
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    ) : (
                                                        <Wrench className="h-3.5 w-3.5" />
                                                    )}
                                                    Fix with AI
                                                </Button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                            {iss.what_good && (
                                                <Field
                                                    label="What's good"
                                                    value={iss.what_good}
                                                    tone="good"
                                                />
                                            )}
                                            {iss.what_missing && (
                                                <Field
                                                    label="What's missing"
                                                    value={iss.what_missing}
                                                    tone="warn"
                                                />
                                            )}
                                            {iss.what_incorrect && (
                                                <Field
                                                    label="What's incorrect"
                                                    value={iss.what_incorrect}
                                                    tone="bad"
                                                />
                                            )}
                                            {iss.why_matters && (
                                                <Field
                                                    label="Why it matters"
                                                    value={iss.why_matters}
                                                />
                                            )}
                                            {iss.recommended_correction && (
                                                <Field
                                                    label="Recommended correction"
                                                    value={
                                                        iss.recommended_correction
                                                    }
                                                    tone="rec"
                                                />
                                            )}
                                            <div>
                                                <div className="label-eyebrow text-slate-500 mb-1">
                                                    Can AI fix this?
                                                </div>
                                                <div
                                                    className={`text-sm ${
                                                        iss.ai_fixable
                                                            ? "text-emerald-300"
                                                            : "text-slate-400"
                                                    }`}
                                                >
                                                    {iss.ai_fixable
                                                        ? "Yes — routes through your Approval Inbox."
                                                        : "Not yet — needs a live connector or manual step."}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="pt-6 flex items-center justify-between border-t border-white/5">
                <div className="text-xs text-slate-500">
                    Audit generated{" "}
                    {new Date(audit.created_at).toLocaleString()}
                </div>
                <Button
                    onClick={() => nav("/approvals")}
                    variant="outline"
                    data-testid="audit-open-approvals"
                    className="border-white/10 bg-white/[0.03] gap-1.5"
                >
                    Open Approval Inbox <ArrowRight className="h-3.5 w-3.5" />
                </Button>
            </div>
        </div>
    );
}

function Field({ label, value, tone }) {
    const toneStyle = {
        good: "text-emerald-300",
        warn: "text-amber-300",
        bad: "text-rose-300",
        rec: "text-cyan-300",
    }[tone];
    return (
        <div>
            <div className={`label-eyebrow mb-1 ${toneStyle || "text-slate-500"}`}>
                {label}
            </div>
            <div className="text-sm text-slate-300 leading-relaxed">
                {value}
            </div>
        </div>
    );
}
